import { Api, BASE_URL, BOTS_BASE_URL } from '@/api/index';
import axios, { AxiosRequestConfig } from 'axios';
import {
  FacebookPage,
  SubscribeFacebookPagePayload,
  SubscribeFacebookPageResult,
  SubscribeInstagramPageResponse,
} from 'ignite360-core';

/** Instagram media row for the post picker (from GET /bots/:id/instagram/recent-media) */
export interface InstagramFeedItem {
  id: string;
  title: string;
  content: string;
  mediaType: string;
  thumbnailUrl: string | null;
  permalink: string | null;
}

export class InstagramApi {
  static async loadFacebookPagesWithConnectedInstagramAccounts(
    facebookUserId: string,
    userAccessToken: string,
  ): Promise<FacebookPage[]> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/instagram/pages`,
      data: {
        facebookUserId,
        accessToken: userAccessToken,
      },
    };
    const result = await axios.request<FacebookPage[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  static async connectBotWithInstagram(
    botId: string,
    data: SubscribeFacebookPagePayload,
  ): Promise<SubscribeInstagramPageResponse> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/instagram/subscribe`,
      data,
    };

    const result = await axios.request<SubscribeInstagramPageResponse>(config);
    if (result.status === 200) {
      return result.data;
    }
    throw new Error('Could not connect bot to instagram!');
  }

  static async disconnectBotFromInstagram(botId: string) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/instagram/unsubscribe`,
    };

    const result = await axios.request<SubscribeFacebookPageResult>(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not disconnect bot from instagram!');
  }

  static async publishTestPost(
    id: string,
    params?: { pageId: string; pageAccessToken: string; businessAccountId: string },
  ): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${id}/instagram/publish-listing`,
      data: params,
    };

    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not publish post.');
  }

  static async getRecentMedia(botId: string): Promise<InstagramFeedItem[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/instagram/recent-media`,
    });
    const result = await axios.request<InstagramFeedItem[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  /** Permanently deletes an Instagram media item on the connected account. */
  static async deleteMedia(botId: string, mediaId: string): Promise<void> {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/instagram/media/${encodeURIComponent(mediaId)}`,
    });
    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not delete Instagram post.');
  }

  /**
   * Downloads a media file through the API (avoids CORS on Instagram CDNs) for post upload.
   */
  static async downloadMediaFile(
    botId: string,
    mediaId: string,
  ): Promise<{ file: File; kind: 'image' | 'video' }> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/instagram/media/${encodeURIComponent(mediaId)}/file`,
      responseType: 'arraybuffer',
    });
    const result = await axios.request<ArrayBuffer>(config);
    if (result.status !== 200 || !result.data) {
      throw new Error('Could not download media.');
    }
    const contentType = (result.headers['content-type'] as string) || 'application/octet-stream';
    const exposeKind = result.headers['x-instagram-media-kind'] as string | undefined;
    const nameMatch = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/.exec(
      (result.headers['content-disposition'] as string) || '',
    );
    const rawName = nameMatch ? decodeURIComponent(nameMatch[1] || nameMatch[2]) : '';
    const inferVideo =
      exposeKind === 'video' || contentType.startsWith('video/') || /\.mp4(\?|$)/i.test(rawName);
    const kind: 'image' | 'video' = inferVideo ? 'video' : 'image';
    const fileName = rawName || `instagram-${mediaId}${kind === 'video' ? '.mp4' : '.jpg'}`;
    const blob = new Blob([result.data], { type: contentType });
    return {
      file: new File([blob], fileName, { type: contentType }),
      kind,
    };
  }
}
